// pages/WebsiteLinks/AddBulkWebsiteLinks.js
import React, { useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

export default function AddBulkWebsiteLinks({
  closeModal,
  refresh,
  handleRefresh,
  chatbotId,
  permission,
}) {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "text/plain") {
      setSelectedFile(file);
      setError(null);
      setResults([]);
    } else {
      setSelectedFile(null);
      setError("Please upload a valid .txt file.");
      setResults([]);
    }
  };

  const handleProcessFile = () => {
    if (!selectedFile) {
      setError("Please select a file first.");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const links = text
        .replace(/,/g, "\n")
        .split("\n")
        .map((link) => link.trim())
        .filter((link) => link);

      if (links.length === 0) {
        setError("No valid links found in the file.");
        return;
      }

      setIsProcessing(true);
      setError(null);
      setResults(links.map((link) => ({ link, status: "pending", message: "Waiting..." })));

      for (const link of links) {
        try {
          const params = chatbotId ? { chatbot_id: chatbotId } : {};
          const response = await axiosInstance.post(
            `/add-website-link`,
            { website_link: link },
            { params }
          );
          setResults((prev) =>
            prev.map((r) =>
              r.link === link
                ? { ...r, status: "success", message: response.data.message || "Success" }
                : r
            )
          );
        } catch (err) {
          setResults((prev) =>
            prev.map((r) =>
              r.link === link
                ? { ...r, status: "error", message: err.response?.data?.message || "Error" }
                : r
            )
          );
        }
      }

      handleRefresh();
      setIsProcessing(false);
    };

    reader.readAsText(selectedFile);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: "background.paper",
        color: "text.primary",
        maxWidth: 700,
        mx: "auto",
      }}
    >
      <Typography variant="h5" fontWeight={700} gutterBottom    color="textPrimary">
        Add <strong>Bulk Website Links</strong>
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
        Upload a <strong>.txt</strong> file with comma-separated or line-separated website links.
      </Typography>

      <TextField
        fullWidth
        type="file"
        inputProps={{ accept: ".txt" }}
        onChange={handleFileChange}
        disabled={isProcessing}
        sx={{
          "& input": { color: "text.primary" },
        }}
      />

      {permission.canModify && (
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={handleProcessFile}
          disabled={isProcessing}
          sx={{ mt: 2 }}
        >
          {isProcessing ? (
            <>
              <CircularProgress size={18} sx={{ mr: 1 }} /> Processing...
            </>
          ) : (
            "Upload and Process Links"
          )}
        </Button>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {results.length > 0 && (
        <Box sx={{ mt: 3, maxHeight: "40vh", overflowY: "auto" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Processing Results
          </Typography>
          <List>
            {results.map((result, index) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{
                    bgcolor:
                      result.status === "success"
                        ? theme.palette.success.light + "20"
                        : result.status === "error"
                        ? theme.palette.error.light + "20"
                        : "transparent",
                    borderRadius: 1,
                  }}
                >
                  <ListItemText
                    primary={`${index + 1}) ${result.link}`}
                    secondary={result.message}
                    primaryTypographyProps={{
                      color: "text.primary",
                      sx: { wordBreak: "break-all" },
                    }}
                    secondaryTypographyProps={{
                      color:
                        result.status === "success"
                          ? "success.main"
                          : result.status === "error"
                          ? "error.main"
                          : "text.secondary",
                    }}
                  />
                </ListItem>
                {index < results.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}
    </Paper>
  );
}
